/**
 * "Remove me" (§7.5) — the leave path. Calls `DELETE /gates/{id}/members` (D18) on confirm.
 *
 * **D144 collapsed this from a route into a popover, and the reason is that it was never a
 * screen.** It was one card, three buttons, and a single entry point — a dialog wearing a route.
 * D143 then made the sole-manager branch an `AlertPopover`, which left the feature rendering a
 * dialog floating over an empty screen in one state and a card in the others; that inconsistency
 * is what made the question worth asking. There is no `removeMe` route, no deep link, and no
 * `RouteId` any more. Opened by Gate Detail's own Remove-me row, which is the only thing that
 * ever navigated here.
 *
 * **Two dialogs, chosen by state, and the split is D143's:**
 *
 * - A **confirmed sole manager** gets `AlertPopover` — one dismiss action, no confirm affordance
 *   anywhere in the component. The server refuses it too (`409 last-manager`), which is the
 *   authority; this is the explanation.
 * - Everyone else gets `ConfirmPopover`, whose confirm waits on the roster.
 *
 * **Which branch a member sees is a roster question, and the roster arrives late** — that hazard
 * predates both decisions and the three-state answering it is carried over intact. It was once a
 * `useState(false)` that only ever moved *up*, so between mount and the fetch landing — and
 * permanently if the fetch failed — a sole manager read the ordinary-member copy: *"you can be
 * re-invited later"*, with the confirm enabled. So the state **fails toward the severe copy** and
 * the confirm is withheld until the answer lands.
 *
 * **`failed` is not `sole`, and that distinction is the one thing here not to simplify.** A roster
 * that would not load is *unknown*. Treating it as sole would tell a resident with a flaky
 * connection that they may never leave a gate they have every right to leave — the original defect
 * wearing D143's clothes. It keeps the retry and withholds the confirm instead.
 *
 * **`knownManagerCount` removes the visible swap, and since D153 it seeds both directions.**
 * Without it a sole manager watched the *confirmation* dialog render, then saw it replaced by the
 * refusal a moment later: two dialogs for one tap, the second of which takes an action away that
 * the first appeared to offer.
 *
 * The seed was originally accepted **only when it said `sole`**, on the reasoning that a stale
 * `shared` would enable the confirm under copy that may be wrong. That asymmetry had a cost nobody
 * priced: a co-managed gate seeds nothing, starts at `'unknown'`, and `severe` is
 * `managerCount !== 'shared'` — so **every ordinary member tapping Remove me got the severe copy
 * with the confirm disabled for the length of a roster round-trip.** Fixing the sole manager's
 * flash had simply moved it onto everyone else, which is how it was reported from a device.
 *
 * **What changed the premise is where the seed comes from.** It was Gate Detail's own async roster
 * read; D153 made it the gate projection's `managerCount`, server-authoritative and already present
 * in the `GET /gates/{id}` response that rendered the row. A seeded `shared` is now the same class
 * of evidence as a seeded `sole`, and the stale case it was guarding against is *already* handled
 * further down: `confirm()` routes a `409 last-manager` to the alert, which is D143's rule that the
 * server is the authority and this dialog is the explanation. Erring toward the confirm therefore
 * costs one refused request, not a gate left without a manager.
 *
 * **2026-08-14: Gate Detail stopped rendering the row at all for a confirmed sole manager**, so
 * the `AlertPopover` branch above is no longer the *normal* way that fact reaches a manager — it
 * is now a defense against a stale read: the row rendered while `managerCount` was still
 * `undefined`/`'failed'` and the confirmed answer arrived only after the tap (`knownManagerCount`
 * absent or stale), or a co-manager left between the row's render and this popover's own roster
 * read confirming it. Both `blocked` and `knownManagerCount === 1` stay exactly as they were —
 * removing the row narrows how often this component's own swap is reachable; it does not replace
 * the guard this component carries, and this component still has no way to know whether Gate
 * Detail decided to show the row it was opened from.
 */
import { useEffect, useState } from 'react'
import type { GateId } from '@selogate/core'
import { useDeviceCache } from '../../device-cache/context'
import { useSession } from '../../auth/context'
import { NetworkError } from '../../auth/transport'
import { AlertPopover, ConfirmPopover } from '../../components'
import { useTranslation } from '../../i18n/I18nProvider'
import { useNavigation } from '../../router'
import { getRoster, removeMyMembership } from '../shared/api'

type ManagerCountState = 'unknown' | 'sole' | 'shared' | 'failed'

/**
 * Whether a state is a **real answer** — one that a later non-answer may not take back.
 *
 * This used to be spelled `current === 'sole'` at each of the four sites below, which was right
 * while only `'sole'` could be seeded: a held `'shared'` could then only have come from this
 * component's own completed fetch, and nothing re-blanked it in practice. **D153 made `'shared'`
 * seedable too**, so the narrow test would blank a seeded `'shared'` back to `'unknown'` on the
 * very first `loadManagerCount()` — restoring the severe-copy flash this seed exists to remove,
 * on the exact path it was reported on.
 */
function isAnswered(state: ManagerCountState): boolean {
  return state === 'sole' || state === 'shared'
}

export function RemoveMePopover({
  gateId,
  knownManagerCount,
  onClose,
}: {
  readonly gateId: GateId
  /** What the opener's own roster read reported, if it has one. Seeds `'sole'` and nothing else. */
  readonly knownManagerCount?: number
  onClose(): void
}): JSX.Element {
  const session = useSession()
  const deviceCache = useDeviceCache()
  const navigate = useNavigation()
  const { t } = useTranslation('removeMe')
  const { t: tCommon } = useTranslation('common')

  /**
   * **`viewerRole` is itself a late arrival, and reading it early re-opened this same hole.**
   *
   * The three-state below asks the roster only when `manager` is true — but `manager` is read off
   * the cached gate, and the cache is empty until the IndexedDB snapshot restores. On a cold entry
   * a manager therefore looked like a resident, took the `'shared'` branch, never fetched the
   * roster, and got the *mild* copy with the confirm **enabled**. `origin` is the cache's "have I
   * answered yet", and until it has, nothing derived from it may be acted on. Same rule as
   * `PeopleScreen`'s.
   *
   * Still true after D144, and *more* reachable than it was: as a route this mounted after a
   * navigation, where as a popover it can open on a Gate Detail reached by deep link on a cold
   * start — the cache is likelier to be mid-restore, not less.
   */
  const cacheAnswered = deviceCache.origin !== undefined
  const cached = deviceCache.gates.find((g) => g.gateId === gateId)
  const manager = cached?.viewerRole === 'manager'

  /**
   * `'unknown'` until the roster answers — for a manager only. A resident never asks (the roster
   * is manager-only, §7.7), and `memberBody` is the whole truth for them, so their state is
   * `'shared'` from the start rather than a fourth case — but only once the cache has actually
   * said they are a resident.
   */
  /**
   * The seed, **both directions since D153** — see the header note. A caller's `shared` is now
   * adopted as readily as its `sole`, because the caller's value is the projection's
   * `managerCount`, not a second async roster read.
   */
  const seededState: 'sole' | 'shared' | undefined =
    knownManagerCount === undefined ? undefined : knownManagerCount <= 1 ? 'sole' : 'shared'
  const [managerCount, setManagerCount] = useState<'unknown' | 'sole' | 'shared' | 'failed'>(
    seededState ?? (cacheAnswered && !manager ? 'shared' : 'unknown'),
  )
  const [removing, setRemoving] = useState(false)
  const [error, setError] = useState(false)

  /**
   * R1-6 — was a bare `await` with nothing to catch it: `getRoster`'s `NetworkError` (offline, a
   * dead cellular link) reached no code here, leaving `managerCount` at `'unknown'` forever —
   * severe copy, "טוען…" hint, and the confirm disabled for the life of the popover, with the
   * designed `'failed'`→retry affordance never reached.
   */
  async function loadManagerCount(): Promise<void> {
    // **A refresh does not un-know.** Blanking to `'unknown'` here is what the seed exists to
    // avoid: it would take the alert back down to the confirmation for the length of the fetch,
    // which is the swap this popover was reported for. An answer already held stands until this
    // call replaces it — including when this call fails, since a dropped roster is not evidence
    // that a manager appeared.
    setManagerCount((current) => (isAnswered(current) ? current : 'unknown'))
    try {
      const result = await getRoster(session.request, gateId)
      // Not silence. Without the roster this cannot say which warning is true, and the one it
      // would otherwise default to is the reassuring one.
      if (!result.ok) {
        setManagerCount((current) => (isAnswered(current) ? current : 'failed'))
        return
      }
      setManagerCount(result.data.members.filter((m) => m.role === 'manager').length <= 1 ? 'sole' : 'shared')
    } catch (error) {
      setManagerCount((current) => (isAnswered(current) ? current : 'failed'))
      if (!(error instanceof NetworkError)) throw error
    }
  }

  useEffect(() => {
    // Not yet knowable. Holding at `'unknown'` keeps the severe copy and the disabled confirm,
    // which is the safe side of both; this re-runs the moment the cache answers.
    if (!cacheAnswered) {
      setManagerCount((current) => (isAnswered(current) ? current : 'unknown'))
      return
    }
    // A seeded `'sole'` came from a manager-only roster read, so the cache calling the viewer a
    // resident here is the *later* fact and wins — the seed is not allowed to outlive its premise.
    if (!manager) {
      setManagerCount('shared')
      return
    }
    void loadManagerCount()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateId, manager, cacheAnswered])

  // Everything that is not a confirmed "there are other managers" gets the severe copy. Testing
  // for `'sole'` alone would leave `unknown` and `failed` falling through to the mild one, which
  // is the original defect in a new shape.
  const severe = managerCount !== 'shared'
  const answered = managerCount === 'sole' || managerCount === 'shared'
  /**
   * D143 — the refusal, keyed on the **confirmed** `'sole'` alone.
   *
   * Deliberately narrower than `severe` above. That one governs *copy*, where erring toward the
   * grave reading is free; this governs whether an action exists at all, and refusing on
   * `unknown`/`failed` would strand a member whose roster call timed out.
   */
  const blocked = managerCount === 'sole'

  /**
   * R1-6 — was a bare `await` too: an offline tap threw unhandled out of the confirm button's
   * `onClick`, and `removing` never came back down, leaving the confirm disabled with no error —
   * dismissible, so escapable, but silent while it lasted.
   */
  async function confirm(): Promise<void> {
    setRemoving(true)
    setError(false)
    try {
      const result = await removeMyMembership(session.request, gateId)
      if (!result.ok) {
        /**
         * **The roster said there was a co-manager and the server disagreed.** Reachable without
         * anyone doing anything wrong: the other manager left, or was demoted, between the roster
         * read and this request. Showing the generic *could not remove* here would be a retry
         * prompt for something that will never succeed, so the refusal switches this popover to
         * D143's alert — which, having no confirm, also takes the action away.
         */
        if (result.code === 'last-manager') {
          setManagerCount('sole')
        } else {
          setError(true)
        }
        return
      }
      void deviceCache.refresh()
      // **`resetTo`, not `onClose`.** Closing would return to a Gate Detail for a gate the viewer
      // no longer belongs to — every read on it now 404s. The membership is gone, so the screen
      // behind this popover is gone with it.
      navigate.resetTo({ id: 'home' })
    } catch (error) {
      setError(true)
      if (!(error instanceof NetworkError)) throw error
    } finally {
      setRemoving(false)
    }
  }

  if (blocked) {
    return (
      <AlertPopover
        title={t('blockedTitle')}
        body={t('blockedBody')}
        dismissLabel={tCommon('cancel')}
        onDismiss={onClose}
      />
    )
  }

  return (
    <ConfirmPopover
      title={t('warningTitle')}
      body={severe ? t('unconfirmedBody') : t('memberBody')}
      {...(managerCount === 'unknown' ? { hint: tCommon('loading') } : {})}
      {...(managerCount === 'failed'
        ? {
            error: t('rosterFailed'),
            secondaryAction: { label: tCommon('retry'), onClick: () => void loadManagerCount() },
          }
        : error
          ? { error: t('removeFailed') }
          : {})}
      cancelLabel={tCommon('cancel')}
      confirmLabel={t('confirm')}
      // `answered` gates the confirm, not `removing` alone. The roster decides *which copy is
      // true*, so offering the action before it lands means offering it under copy that may be
      // wrong — and the way it is wrong is that the gate ends up with no manager.
      confirmDisabled={removing || !answered}
      onCancel={onClose}
      onConfirm={() => void confirm()}
    />
  )
}
